import React from 'react'
import styled from 'styled-components'
import { useNavigate } from 'react-router-dom'
import { AppPrimaryButton } from 'elements'
import { useSubscriptionUsage } from 'context/subscription-usage'
import {
    ACCOUNT_CHECKOUT_PATH,
    ENABLE_FREE_PLAN_USAGE_UI,
    ENABLE_LICENSE_SHOPPING_CART,
    barWidthPercent,
    getBarTone,
    isFreeTier,
} from 'utility/subscription-usage'

const Strip = styled.div`
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    padding: 14px 18px;
    margin-bottom: 16px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    flex-wrap: wrap;
    .meta {
        min-width: 140px;
    }
    .meta .lbl {
        font-size: 12px;
        color: #6b7280;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.05em;
    }
    .meta .val {
        font-size: 16px;
        font-weight: 700;
        color: #111827;
        margin-top: 4px;
    }
    .meta .val span {
        color: #6b7280;
        font-weight: 500;
        font-size: 14px;
    }
    .progress {
        flex: 1;
        min-width: 160px;
        margin: 0 8px;
    }
    .bar {
        background: #e5e7eb;
        height: 8px;
        border-radius: 999px;
        overflow: hidden;
    }
    .bar-fill {
        height: 100%;
        border-radius: 999px;
    }
    .bar-fill.ok {
        background: #2563eb;
    }
    .bar-fill.warn {
        background: #f59e0b;
    }
    .bar-fill.over {
        background: #dc2626;
    }
    .hint {
        font-size: 11px;
        margin-top: 6px;
    }
    .hint.warn {
        color: #92400e;
    }
    .hint.over {
        color: #991b1b;
    }
    .actions {
        display: flex;
        gap: 8px;
        flex-wrap: wrap;
        align-items: center;
    }
    @media (max-width: 930px) {
        flex-direction: column;
        align-items: stretch;
        .meta,
        .progress,
        .actions {
            width: 100%;
            margin: 0;
            min-width: 0;
        }
        .actions button,
        .actions a {
            width: 100%;
        }
    }
`

/**
 * Inline Free-plan usage strip for Users or Templates pages.
 * @param {'seats'|'templates'} metric
 * @param {React.ReactNode} actions optional buttons rendered on the right
 * @param {boolean} showUpgrade show one Upgrade action when near or over the cap
 */
export const PlanUsageStrip = ({
    metric = 'seats',
    actions = null,
    showUpgrade = false,
}) => {
    const navigate = useNavigate()
    const { freeUsage } = useSubscriptionUsage()
    if (!ENABLE_FREE_PLAN_USAGE_UI) return null
    if (!freeUsage || !isFreeTier(freeUsage.tier)) return null

    const data =
        metric === 'templates' ? freeUsage.activeTemplates : freeUsage.seats
    if (!data || data.cap == null) return null

    const tone = getBarTone(data)
    const remaining = Math.max(0, data.cap - data.current)
    const label = metric === 'templates' ? 'Active templates' : 'Seats'
    const unit = metric === 'templates' ? 'active templates' : 'seats'
    let hint = null
    if (tone === 'over') {
        hint = `Free plan includes ${data.cap} ${unit}. Upgrade to add more.`
    } else if (tone === 'warn') {
        hint = `${remaining} ${unit} left on the Free plan.`
    }
    const showUpgradeButton =
        ENABLE_LICENSE_SHOPPING_CART &&
        showUpgrade &&
        (tone === 'over' || tone === 'warn')

    return (
        <Strip>
            <div className="meta">
                <div className="lbl">{label}</div>
                <div className="val">
                    {data.current} <span>of {data.cap}</span>
                </div>
            </div>
            <div className="progress">
                <div className="bar">
                    <div
                        className={`bar-fill ${tone}`}
                        style={{ width: `${barWidthPercent(data)}%` }}
                    />
                </div>
                {hint && <div className={`hint ${tone}`}>{hint}</div>}
            </div>
            {(actions || showUpgradeButton) && (
                <div className="actions">
                    {actions}
                    {showUpgradeButton && (
                        <AppPrimaryButton
                            type="button"
                            onClick={() => navigate(ACCOUNT_CHECKOUT_PATH)}
                        >
                            Upgrade plan
                        </AppPrimaryButton>
                    )}
                </div>
            )}
        </Strip>
    )
}
